import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import {
  DistributionBarCard,
  type DistributionBarItem,
} from '@/modules/analytics/components/DistributionBarCard/DistributionBarCard';
import { useComparisonMode } from '@/modules/analytics/hooks/useComparisonMode';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { AbsoluteChangeIndicator } from './AbsoluteChangeIndicator';
import { useGoalDistribution } from './hooks/useAiAgentsDistributions';
import { getComparisonPeriodKey } from './PercentChangeIndicator';

const ACHIEVED_DELTA_PRECISION = 1;

type GoalAchievedCardProps = { className?: string };

export const GoalAchievedCard = ({ className }: GoalAchievedCardProps) => {
  const { t } = useTranslation();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const { isComparing } = useComparisonMode();
  const [isExporting, setIsExporting] = useState(false);

  const { current, previous } = useGoalDistribution();

  const items = useMemo<DistributionBarItem[]>(
    () => [
      {
        id: 'not_achieved',
        label: t('analytics.goal_not_achieved'),
        value: current.not_achieved,
        previousValue: isComparing ? previous.not_achieved : undefined,
        barClassName: 'bg-chart-2',
      },
      {
        id: 'partially',
        label: t('analytics.goal_partially'),
        tooltipLabel: t('analytics.goal_partially_achieved'),
        value: current.partially,
        previousValue: isComparing ? previous.partially : undefined,
        barClassName: 'bg-chart-4',
      },
      {
        id: 'achieved',
        label: t('analytics.goal_achieved'),
        value: current.achieved,
        previousValue: isComparing ? previous.achieved : undefined,
        barClassName: 'bg-chart-3',
      },
    ],
    [current, previous, isComparing, t]
  );

  const handleExport = useCallback(
    (exportFormat: ExportFormat) => {
      try {
        setIsExporting(true);
        const filename = buildExportFilename('goal_achieved');
        const rows = items.map(({ id, label, value }) => ({ id, label, percentage: value }));

        if (exportFormat === 'json') {
          downloadJson({ goal_achieved: rows }, filename);
        } else {
          downloadCsv(rows, filename);
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [items, t]
  );

  return (
    <DistributionBarCard
      className={className}
      title={t('analytics.goal_achieved_title')}
      titleTooltip={t('analytics.goal_achieved_title_tooltip')}
      description={t('analytics.classified_conversations', { count: current.totalCount })}
      items={items}
      isEmpty={current.totalCount === 0}
      exportConfig={{ onExport: handleExport, isExporting }}
      barSizing="fill"
      barRadiusClassName="rounded-md"
      showValueAboveBar
      emptyState={{
        title: t('analytics.no_data_available'),
        description: t('analytics.no_data_for_period'),
      }}
      footer={
        isComparing ? (
          <AbsoluteChangeIndicator
            current={current.achieved}
            previous={previous.achieved}
            formatDelta={(delta) => {
              const abs = Math.abs(delta).toFixed(1);
              if (delta > 0) {
                return t('analytics.compare_goal_achieved_more', { 0: abs });
              }
              if (delta < 0) {
                return t('analytics.compare_goal_achieved_less', { 0: abs });
              }
              return t('analytics.compare_goal_achieved_unchanged');
            }}
            precision={ACHIEVED_DELTA_PRECISION}
            comparisonLabel={t(getComparisonPeriodKey(startDate, endDate))}
          />
        ) : null
      }
    />
  );
};
